import { fmtNum, fmtPct, fmtUsd } from "@/lib/api";
import { HintLabel } from "@/components/Hint";
import { Locked } from "@/components/Locked";

export const Stat = ({ k, label, value, hint }) => (
  <div className="apx-card p-3">
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500"><HintLabel k={k}>{label}</HintLabel></div>
    <div className="font-mono-ticker text-sm font-semibold mt-0.5">{value}</div>
    {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
  </div>
);

const Metric = ({ k, label, value, hint }) => (
  <div>
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500"><HintLabel k={k}>{label}</HintLabel></div>
    <div className="font-mono-ticker font-medium">{value ?? "—"}</div>
    {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
  </div>
);

export const CoinKeyStats = ({ md }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    <Stat k="market_cap" label="Market Cap" value={"$" + fmtNum(md.market_cap?.usd)} />
    <Stat k="volume_24h" label="24h Volume" value={"$" + fmtNum(md.total_volume?.usd)} />
    <Stat k="circulating" label="Circulating" value={fmtNum(md.circulating_supply, 0)} />
    <Stat k="ath" label="ATH" value={fmtUsd(md.ath?.usd)} hint={fmtPct(md.ath_change_percentage?.usd)} />
  </div>
);

export const CoinIndicators = ({ ind, sr }) => (
  <Locked feature="indicators" title="Advanced technical indicators" compact desc="RSI, MACD, SMA/EMA stack, Bollinger Bands, momentum, volatility and programmatic support/resistance for every coin.">
  <div className="apx-card p-5" data-testid="indicators-card">
    <div className="flex items-center justify-between mb-3">
      <div className="font-display text-base font-semibold">Technical Snapshot</div>
      {ind?.series_as_of && <div className="text-[10px] font-mono-ticker text-slate-500" data-testid="indicators-as-of">Data as of {new Date(ind.series_as_of).toLocaleString()}</div>}
    </div>
    {ind && ind.data_fresh === false ? (
      <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 p-3" data-testid="indicators-data-delayed">
        <div className="font-mono-ticker text-xs tracking-widest text-amber-300 font-semibold">DATA DELAYED</div>
        <div className="text-xs text-amber-100 mt-1">Indicators withheld until current market data is received (stale values are never shown as current).</div>
      </div>
    ) : ind?.insufficient ? (
      <div className="text-xs text-slate-400">Insufficient history to compute indicators.</div>
    ) : ind ? (
      <div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Metric k="rsi" label="RSI (14)" value={ind.rsi?.toFixed(2)} hint={ind.rsi > 70 ? "Overbought" : ind.rsi < 30 ? "Oversold" : "Neutral"} />
          <Metric k="macd" label="MACD" value={ind.macd?.toFixed(4)} hint={ind.macd_hist > 0 ? "Bullish cross" : "Bearish cross"} />
          <Metric k="sma20" label="SMA 20" value={fmtUsd(ind.sma20)} />
          <Metric k="sma50" label="SMA 50" value={fmtUsd(ind.sma50)} />
          <Metric k="sma200" label="SMA 200" value={fmtUsd(ind.sma200)} />
          <Metric k="ema20" label="EMA 20" value={fmtUsd(ind.ema20)} />
          <Metric k="bb_upper" label="BB Upper" value={fmtUsd(ind.bb_upper)} />
          <Metric k="bb_lower" label="BB Lower" value={fmtUsd(ind.bb_lower)} />
          <Metric k="momentum" label="Momentum 10d" value={fmtPct(ind.momentum_10)} />
          <Metric k="volatility" label="Volatility 30d" value={ind.volatility_30d?.toFixed(2) + "%"} />
        </div>
        {sr && (
          <div className="mt-4 pt-3 border-t border-slate-800 grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-testid="indicators-sr">
            {(sr.supports || []).slice(0, 2).map((v, i) => <Metric key={`sup-${v}`} k="support" label={`Support ${i + 1}`} value={<span className="text-emerald-300">{fmtUsd(v)}</span>} />)}
            {(sr.resistances || []).slice(0, 2).map((v, i) => <Metric key={`res-${v}`} k="resistance" label={`Resistance ${i + 1}`} value={<span className="text-red-300">{fmtUsd(v)}</span>} />)}
          </div>
        )}
      </div>
    ) : <div className="skeleton h-24 w-full" />}
  </div>
  </Locked>
);
